/**
 * Solid (filled) glyphs for the command-center design — KPI tiles, quick
 * actions, announcements and the navy rail all use filled icons, which the
 * app's stroke set (the `lucide-react` shim) can't produce. 24×24,
 * `currentColor`. Dashboard glyphs paint knock-outs in the card colour; the
 * `Nav*` glyphs use even-odd holes instead, so they work on any background.
 */
import type { SVGProps } from "react";

type P = { size?: number } & Omit<SVGProps<SVGSVGElement>, "fill">;
const KO = "var(--cc-card, #fff)"; // knock-out colour

function Svg({ size = 24, children, ...rest }: P & { children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false" {...rest}>
      {children}
    </svg>
  );
}

export function SolidUsers(p: P) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="7.5" r="3.6" />
      <path d="M2.5 19.2c0-3.7 2.9-6.2 6.5-6.2s6.5 2.5 6.5 6.2v.8h-13z" />
      <circle cx="17.2" cy="8.6" r="2.8" opacity=".6" />
      <path d="M16.6 13.2c2.9.2 4.9 2.3 4.9 5.3V20h-4.3v-.8c0-2.4-.3-4.3-.6-6z" opacity=".6" />
    </Svg>
  );
}

export function SolidPresentation(p: P) {
  return (
    <Svg {...p}>
      <path d="M3 3.5h18a1 1 0 0 1 1 1v.5a1 1 0 0 1-1 1h-.5v9a1.5 1.5 0 0 1-1.5 1.5h-6v1.6l2.8 2.2-1.1 1.4L12 19.4l-2.7 2.3-1.1-1.4L11 18.1v-1.6H5a1.5 1.5 0 0 1-1.5-1.5V6H3a1 1 0 0 1-1-1v-.5a1 1 0 0 1 1-1z" />
      <rect x="7" y="10.5" width="2" height="3" rx=".5" fill={KO} />
      <rect x="11" y="8.5" width="2" height="5" rx=".5" fill={KO} />
      <rect x="15" y="9.5" width="2" height="4" rx=".5" fill={KO} />
    </Svg>
  );
}

export function SolidCalendarCheck(p: P) {
  return (
    <Svg {...p}>
      <path d="M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2z" />
      <rect x="2.5" y="8" width="19" height="1.4" fill={KO} />
      <path d="m8.3 14.4 2.4 2.4 5-5" fill="none" stroke={KO} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function SolidWallet(p: P) {
  return (
    <Svg {...p}>
      <path d="M5 4h12.5A1.5 1.5 0 0 1 19 5.5V7H5a1 1 0 0 0 0 2h14.5A2.5 2.5 0 0 1 22 11.5v7a2.5 2.5 0 0 1-2.5 2.5H5A3 3 0 0 1 2 18V7a3 3 0 0 1 3-3z" />
      <rect x="14.5" y="12.5" width="5" height="4" rx="1.4" fill={KO} />
      <circle cx="16.6" cy="14.5" r="1" />
    </Svg>
  );
}

export function SolidDocCheck(p: P) {
  return (
    <Svg {...p}>
      <path d="M6 2h8.2L20 7.8V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
      <path d="M14 2v4.5A1.5 1.5 0 0 0 15.5 8H20" fill={KO} opacity=".45" />
      <rect x="7.5" y="10.5" width="6" height="1.5" rx=".75" fill={KO} />
      <path d="m8.2 16.2 2 2 4.3-4.3" fill="none" stroke={KO} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function SolidTicket(p: P) {
  return (
    <Svg {...p}>
      <path d="M4.5 5h15A2.5 2.5 0 0 1 22 7.5v2.2a2.4 2.4 0 0 0 0 4.6v2.2a2.5 2.5 0 0 1-2.5 2.5h-15A2.5 2.5 0 0 1 2 16.5v-2.2a2.4 2.4 0 0 0 0-4.6V7.5A2.5 2.5 0 0 1 4.5 5z" />
      <path d="M15 7v2m0 2v2m0 2v2" stroke={KO} strokeWidth="1.6" strokeLinecap="round" />
    </Svg>
  );
}

export function SolidUserPlus(p: P) {
  return (
    <Svg {...p}>
      <circle cx="9.5" cy="7.5" r="3.8" />
      <path d="M2.5 19.5c0-3.8 3.1-6.5 7-6.5s7 2.7 7 6.5v.5h-14z" />
      <path d="M19 8v6M16 11h6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </Svg>
  );
}

export function SolidChalkboard(p: P) {
  return (
    <Svg {...p}>
      <rect x="2" y="3.5" width="20" height="13.5" rx="2" />
      <path d="m6.5 11.3 2.3 2.2 4.4-4.4" fill="none" stroke={KO} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="15" y="9" width="4" height="1.5" rx=".75" fill={KO} />
      <rect x="15" y="12" width="3" height="1.5" rx=".75" fill={KO} />
      <path d="M8 21l2-4h4l2 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Svg>
  );
}

export function SolidCash(p: P) {
  return (
    <Svg {...p}>
      <rect x="2" y="5.5" width="20" height="13" rx="2.5" />
      <circle cx="12" cy="12" r="3.2" fill={KO} />
      <path d="M11 10.6h2.2M11 11.9h2.2M11.3 10.6c1.4 0 1.4 2.6 0 2.6l1.9 1.5" fill="none" stroke="currentColor" strokeWidth=".9" strokeLinecap="round" />
      <circle cx="5.8" cy="12" r="1" fill={KO} />
      <circle cx="18.2" cy="12" r="1" fill={KO} />
    </Svg>
  );
}

export function SolidMegaphone(p: P) {
  return (
    <Svg {...p}>
      <path d="M17 4.2v15.6a.8.8 0 0 1-1.2.7L9.6 16.8H6.5l1.3 4.1a.8.8 0 0 1-.8 1.1H5.4a.8.8 0 0 1-.8-.6L3.2 16.6A3.5 3.5 0 0 1 2 14V10a3.5 3.5 0 0 1 3.5-3.5h4.1l6.2-3a.8.8 0 0 1 1.2.7z" />
      <rect x="18.8" y="9.2" width="3" height="5.6" rx="1.5" />
    </Svg>
  );
}

export function SolidBarChart(p: P) {
  return (
    <Svg {...p}>
      <rect x="3" y="12" width="4" height="9" rx="1.2" />
      <rect x="10" y="6" width="4" height="15" rx="1.2" />
      <rect x="17" y="3" width="4" height="18" rx="1.2" />
    </Svg>
  );
}

export function SolidFlask(p: P) {
  return (
    <Svg {...p}>
      <path d="M8.5 2h7a1 1 0 0 1 0 2H15v4.8l5.4 9.6A2.4 2.4 0 0 1 18.3 22H5.7a2.4 2.4 0 0 1-2.1-3.6L9 8.8V4h-.5a1 1 0 0 1 0-2z" />
      <path d="M7.3 15h9.4" stroke={KO} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10.5" cy="18" r="1" fill={KO} />
      <circle cx="14" cy="17.6" r=".8" fill={KO} />
    </Svg>
  );
}

export function SolidRupee(p: P) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8.3 7.6h7.4M8.3 10.6h7.4M9 7.6c4.4 0 4.4 6 0 6h-.7l5.6 4.2" fill="none" stroke={KO} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function SolidCalendar(p: P) {
  return (
    <Svg {...p}>
      <path d="M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2z" />
      <rect x="2.5" y="8" width="19" height="1.4" fill={KO} />
      {[7, 12, 17].map((x) => [12.5, 16.5].map((y) => <rect key={`${x}-${y}`} x={x - 1.3} y={y - 1.2} width="2.6" height="2.4" rx=".6" fill={KO} />))}
    </Svg>
  );
}

export function SolidSun(p: P) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1.5v2.5M12 20v2.5M1.5 12H4M20 12h2.5M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Svg>
  );
}

export function SolidAlert(p: P) {
  return (
    <Svg {...p}>
      <path d="M10.3 3.3a2 2 0 0 1 3.4 0l8.4 14.6A2 2 0 0 1 20.4 21H3.6a2 2 0 0 1-1.7-3.1z" />
      <rect x="11" y="8.5" width="2" height="6.5" rx="1" fill={KO} />
      <circle cx="12" cy="17.6" r="1.2" fill={KO} />
    </Svg>
  );
}

export function SolidPin(p: P) {
  return (
    <Svg {...p}>
      <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7z" />
      <circle cx="12" cy="9" r="2.6" fill={KO} />
    </Svg>
  );
}

// ── Navigation glyphs (even-odd holes, no knock-out colour) ────────────────

function Solid({ d, ...p }: P & { d: string }) {
  return (
    <Svg {...p}>
      <path d={d} fillRule="evenodd" clipRule="evenodd" />
    </Svg>
  );
}

export const NavHome = (p: P) => <Solid {...p} d="M11.3 2.7a1 1 0 0 1 1.4 0l9 8.3a.8.8 0 0 1-.5 1.4h-1.7V20a1.5 1.5 0 0 1-1.5 1.5h-3.8v-5.8H9.8v5.8H6A1.5 1.5 0 0 1 4.5 20v-7.6H2.8a.8.8 0 0 1-.5-1.4z" />;
export const NavStudents = (p: P) => <Solid {...p} d="M9 3.9a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2zM2.5 19.2c0-3.7 2.9-6.2 6.5-6.2s6.5 2.5 6.5 6.2v.8h-13zM17.2 5.8a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6zm-.6 7.4c2.9.2 4.9 2.3 4.9 5.3V20h-4.3v-.8c0-2.4-.3-4.3-.6-6z" />;
export const NavTeacher = (p: P) => <Solid {...p} d="M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM4 20c0-4 3.6-7 8-7s8 3 8 7v1H4z" />;
export const NavLayers = (p: P) => <Solid {...p} d="M12 2.5l9.5 5-9.5 5-9.5-5zM4.2 11.2 12 15.3l7.8-4.1 1.7.9-9.5 5-9.5-5zm0 4.2L12 19.5l7.8-4.1 1.7.9-9.5 5-9.5-5z" />;
export const NavExam = (p: P) => <Solid {...p} d="M6 2h8.5L20 7.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm2 10v1.6h8V12zm0 4v1.6h6V16z" />;
export const NavAttendance = (p: P) => <Solid {...p} d="M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2zM8 14.2l1.3-1.3 1.9 1.9 4.4-4.4 1.3 1.3-5.7 5.7z" />;
export const NavTimetable = (p: P) => <Solid {...p} d="M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2zM5.5 10v2.5H8V10zm5.2 0v2.5h2.6V10zm5.3 0v2.5h2.5V10zM5.5 15v2.5H8V15zm5.2 0v2.5h2.6V15zm5.3 0v2.5h2.5V15z" />;
export const NavClipboard = (p: P) => <Solid {...p} d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1zm-1 9v1.6h8V11zm0 4v1.6h5V15z" />;
export const NavBook = (p: P) => <Solid {...p} d="M2 4.5c3.5-1 6.5-.6 9 1.2V20c-2.5-1.6-5.5-2-9-1.2zm20 0c-3.5-1-6.5-.6-9 1.2V20c2.5-1.6 5.5-2 9-1.2z" />;
export const NavAward = (p: P) => <Solid {...p} d="M12 2.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 3.2L10.9 8l-2.4.3 1.8 1.7-.5 2.4 2.2-1.2 2.2 1.2-.5-2.4 1.8-1.7-2.4-.3zM8.4 16.2a7.9 7.9 0 0 0 7.2 0L17 22l-5-2.6L7 22z" />;
export const NavAdmission = (p: P) => <Solid {...p} d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1zm2.2 8v2.7H8.5v1.6h2.7V17h1.6v-2.7h2.7v-1.6h-2.7V10z" />;
export const NavCard = (p: P) => <Solid {...p} d="M4 5h16a2 2 0 0 1 2 2v1.5H2V7a2 2 0 0 1 2-2zM2 11h20v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm3 3.5V16h5v-1.5z" />;
export const NavBus = (p: P) => <Solid {...p} d="M6 3h12a3 3 0 0 1 3 3v11a2 2 0 0 1-1 1.7V20a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1h-9v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-1.3A2 2 0 0 1 3 17V6a3 3 0 0 1 3-3zM5.5 7v4.5h13V7zM7.2 14a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6zm9.6 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6z" />;
export const NavBuilding = (p: P) => <Solid {...p} d="M4 3h11a1 1 0 0 1 1 1v6h4a1 1 0 0 1 1 1v10H3V4a1 1 0 0 1 1-1zm3 4v2h2V7zm4 0v2h2V7zm-4 4v2h2v-2zm4 0v2h2v-2zm6 2v2h2v-2zm-9 3.5V21h3v-4.5zm9 .5v2h2v-2z" />;
export const NavBox = (p: P) => <Solid {...p} d="M12 2l9 4.5v11L12 22l-9-4.5v-11zm0 2.3L6.2 7.1 12 10l5.8-2.9z" />;
export const NavLibrary = (p: P) => <Solid {...p} d="M3 4h3.6v16H3zm5 0h3.6v16H8zm5.1 1.3 3.5-.9 4.2 15.4-3.5.9z" />;
export const NavBriefcase = (p: P) => <Solid {...p} d="M9 3h6a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v4H2V8a2 2 0 0 1 2-2h3V5a2 2 0 0 1 2-2zm0 2v1h6V5zM2 13.5h8.5V15h3v-1.5H22V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" />;
export const NavMegaphone = (p: P) => <Solid {...p} d="M17 4.2v15.6a.8.8 0 0 1-1.2.7L9.6 16.8H6.5l1.3 4.1a.8.8 0 0 1-.8 1.1H5.4a.8.8 0 0 1-.8-.6L3.2 16.6A3.5 3.5 0 0 1 2 14v-4a3.5 3.5 0 0 1 3.5-3.5h4.1l6.2-3a.8.8 0 0 1 1.2.7zM18.8 10.7a1.5 1.5 0 0 1 3 0v2.6a1.5 1.5 0 0 1-3 0z" />;
export const NavEnvelope = (p: P) => <Solid {...p} d="M4 4h16a2 2 0 0 1 2 2v.4l-10 6.2L2 6.4V6a2 2 0 0 1 2-2zM2 8.8l9.5 5.9a1 1 0 0 0 1 0L22 8.8V18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" />;
export const NavChat = (p: P) => <Solid {...p} d="M4 3h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-8l-5 4v-4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm3 5v1.8h10V8zm0 3.6v1.8h6v-1.8z" />;
export const NavChart = (p: P) => <Solid {...p} d="M3 12h4v9H3zm7-6h4v15h-4zm7-3h4v18h-4z" />;
export const NavReport = (p: P) => <Solid {...p} d="M6 2h8.5L20 7.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm1.8 13.5V18h2v-2.5zm3.2-3V18h2v-5.5zm3.2-2V18h2v-7.5z" />;
export const NavBot = (p: P) => <Solid {...p} d="M11 2h2v3h5a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h5zM8.5 10a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm7 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM9 15.3v1.6h6v-1.6z" />;
export const NavShield = (p: P) => <Solid {...p} d="M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5zm-1.2 12.7 5-5-1.3-1.3-3.7 3.7-1.8-1.8-1.3 1.3z" />;
export const NavGear = (p: P) => <Solid {...p} d="M10.3 2h3.4l.5 2.6a8 8 0 0 1 1.9 1.1l2.5-.9 1.7 2.9-2 1.7a8 8 0 0 1 0 2.2l2 1.7-1.7 2.9-2.5-.9a8 8 0 0 1-1.9 1.1l-.5 2.6h-3.4l-.5-2.6a8 8 0 0 1-1.9-1.1l-2.5.9-1.7-2.9 2-1.7a8 8 0 0 1 0-2.2l-2-1.7 1.7-2.9 2.5.9a8 8 0 0 1 1.9-1.1zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />;
export const NavSparkle = (p: P) => <Solid {...p} d="M11 2l2.1 6 6 2.1-6 2.1L11 18.3l-2.1-6-6-2.1 6-2.1zm7.5 11.5l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z" />;
export const NavEvents = (p: P) => <Solid {...p} d="M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2zm7 11v4h4v-4z" />;

// ── Small UI glyphs ────────────────────────────────────────────────────────

export const ArrowUpSolid = (p: P) => <Solid {...p} d="M12 3.5 19 11h-4.5v9.5h-5V11H5z" />;
export const ArrowDownSolid = (p: P) => <Solid {...p} d="M12 20.5 5 13h4.5V3.5h5V13H19z" />;
export const DashSolid = (p: P) => <Solid {...p} d="M5 10.5h14v3H5z" />;

export function ChevronDownBold({ size = 16, ...rest }: P) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false" {...rest}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function SearchBold({ size = 16, ...rest }: P) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden focusable="false" {...rest}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5" />
    </svg>
  );
}

export function RupeeMark({ size = 24, ...rest }: P) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false" {...rest}>
      <path d="M6.5 4.5h11M6.5 9.5h11M8 4.5c5.5 0 5.5 10 0 10H7l8 6" />
    </svg>
  );
}

// ── Directory / table glyphs (filled or heavy-stroke — never hairline) ─────

function Bold({ size = 16, children, ...rest }: P & { children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false" {...rest}>
      {children}
    </svg>
  );
}

export const UiPlus = (p: P) => <Bold {...p}><path d="M12 5v14M5 12h14" /></Bold>;
export const UiClose = (p: P) => <Bold {...p}><path d="M6 6l12 12M18 6 6 18" /></Bold>;
export const UiCheck = (p: P) => <Bold {...p} strokeWidth={3}><path d="m5 12.5 4.5 4.5L19 7.5" /></Bold>;
export const UiMinus = (p: P) => <Bold {...p} strokeWidth={3}><path d="M6 12h12" /></Bold>;
export const UiChevronLeft = (p: P) => <Bold {...p}><path d="m15 5-7 7 7 7" /></Bold>;
export const UiChevronRight = (p: P) => <Bold {...p}><path d="m9 5 7 7-7 7" /></Bold>;
export const UiSort = (p: P) => <Bold {...p} strokeWidth={2.2}><path d="m8 9 4-4 4 4M8 15l4 4 4-4" /></Bold>;
export const UiReset = (p: P) => <Bold {...p}><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" /></Bold>;
export const UiDownload = (p: P) => <Bold {...p}><path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M4.5 19.5h15" /></Bold>;
export const UiUpload = (p: P) => <Bold {...p}><path d="M12 15.5V4.5m0 0L7.5 9M12 4.5 16.5 9M4.5 19.5h15" /></Bold>;
export const UiHash = (p: P) => <Bold {...p}><path d="M9.5 4 8 20M16 4l-1.5 16M4.5 9h16M3.5 15h16" /></Bold>;

export const UiEye = (p: P) => <Solid {...p} d="M12 5c5 0 8.6 3.3 10 7-1.4 3.7-5 7-10 7S3.4 15.7 2 12c1.4-3.7 5-7 10-7zm0 3.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6zm0 2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6z" />;
export const UiPencil = (p: P) => <Solid {...p} d="M16.3 3.3a2 2 0 0 1 2.8 0l1.6 1.6a2 2 0 0 1 0 2.8L9.2 19.2 4 20l.8-5.2zM3 21.5h18V20H3z" />;
export const UiDots = (p: P) => <Solid {...p} d="M5 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />;
export const UiFilter = (p: P) => <Solid {...p} d="M3 4h18l-7 8.2V19l-4 2v-8.8z" />;
export const UiGrid = (p: P) => <Solid {...p} d="M4 4h6.5v6.5H4zm9.5 0H20v6.5h-6.5zM4 13.5h6.5V20H4zm9.5 0H20V20h-6.5z" />;
export const UiTable = (p: P) => <Solid {...p} d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 5v3.2h4V9zm6 0v3.2h8V9zm-6 5.2V18h4v-3.8zm6 0V18h8v-3.8z" />;
export const UiTerminal = (p: P) => <Solid {...p} d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm2.4 4.6-1.1 1.1 2.5 2.4-2.5 2.4 1.1 1.1L10 12.1zM11 15v1.6h6V15z" />;
export const UiKey = (p: P) => <Solid {...p} d="M8 7a5 5 0 0 1 9.7 1.7H21v3h-1.5v2.6h-2.6v-2.6h-2.2A5 5 0 1 1 8 7zm0 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />;

export const UiMale = (p: P) => <Solid {...p} d="M9.5 8a6 6 0 0 1 5 2.7l3.6-3.6H15V5h7v7h-2.1V8.6l-3.8 3.8A6 6 0 1 1 9.5 8zm0 2.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z" />;
export const UiFemale = (p: P) => <Solid {...p} d="M12 2a6 6 0 0 1 1.1 11.9V16H16v2.1h-2.9V22h-2.2v-3.9H8V16h2.9v-2.1A6 6 0 0 1 12 2zm0 2.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z" />;

export const SolidUserCheck = (p: P) => <Solid {...p} d="M9 3.9a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2zM2.5 19.2c0-3.7 2.9-6.2 6.5-6.2 1.6 0 3 .5 4.1 1.3a6 6 0 0 0 .6 5.7H2.5zm13.6-4.6 1.6 1.6 3.5-3.5 1.3 1.3-4.8 4.8-2.9-2.9z" />;
export const SolidUserClock = (p: P) => <Solid {...p} d="M9 3.9a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2zM2.5 19.2c0-3.7 2.9-6.2 6.5-6.2 1.5 0 2.8.4 3.9 1.1A5.5 5.5 0 0 0 13 20H2.5zM18 12.5a4.8 4.8 0 1 1 0 9.6 4.8 4.8 0 0 1 0-9.6zm-.8 2v3.2l2.6 1.6.8-1.2-1.9-1.2v-2.4z" />;
export const SolidUserExit = (p: P) => <Solid {...p} d="M9 3.9a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2zM2.5 19.2c0-3.7 2.9-6.2 6.5-6.2 1.9 0 3.6.7 4.8 1.8v1.3h-1.5v3.9H2.5zm15.3-5.7 4 3.5-4 3.5v-2.3h-4.3v-2.4h4.3z" />;
